import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Users, ArrowRight, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const MigrateClientsModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  selectedClients = [],
  currentPartnerId = null 
}) => {
  const [partners, setPartners] = useState([]);
  const [targetPartnerId, setTargetPartnerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchPartners();
      setTargetPartnerId('');
    }
  }, [isOpen]);

  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await axios.get(`${API}/users/partners`, getAuthHeaders());
      // Ordenar por nome
      const sortedPartners = response.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setPartners(sortedPartners);
    } catch (error) {
      toast.error('Erro ao carregar parceiros');
    } finally {
      setLoadingPartners(false);
    }
  };

  const handleMigrate = async () => {
    if (!targetPartnerId || selectedClients.length === 0) return;

    setLoading(true);
    try {
      await axios.put(`${API}/clients/migrate`, {
        client_ids: selectedClients,
        target_partner_id: targetPartnerId
      }, getAuthHeaders());

      const targetPartner = partners.find(p => p.id === targetPartnerId);
      toast.success(`${selectedClients.length} cliente(s) migrado(s) para ${targetPartner?.name || 'novo parceiro'}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao migrar clientes');
    } finally {
      setLoading(false);
    }
  };

  // Filtrar parceiros para não mostrar o parceiro atual (se houver)
  const availablePartners = currentPartnerId 
    ? partners.filter(p => p.id !== currentPartnerId)
    : partners;

  const targetPartner = partners.find(p => p.id === targetPartnerId);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="migrate-clients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="migrate-modal-title">
            <Users className="w-5 h-5 text-primary" />
            Migrar Clientes para Outro Parceiro
          </DialogTitle>
          <DialogDescription>
            Transfira os clientes selecionados para outro parceiro. Esta ação será registrada na auditoria.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Info sobre clientes selecionados */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Clientes selecionados:</span>
              <Badge variant="secondary" className="text-lg px-3">
                {selectedClients.length}
              </Badge>
            </div>
          </div>

          {/* Alerta de informação */}
          <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800">
            <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200 text-sm">
              <strong>O que será mantido:</strong>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Período/Lote atual</li>
                <li>Data de cadastro original</li>
                <li>Documentos anexados</li>
                <li>Status de pagamento</li>
                <li>Status do processo</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Seleção do parceiro destino */}
          <div className="space-y-2">
            <Label htmlFor="target-partner">Selecionar Novo Parceiro *</Label>
            {loadingPartners ? (
              <div className="text-sm text-muted-foreground py-2">Carregando parceiros...</div>
            ) : (
              <Select value={targetPartnerId} onValueChange={setTargetPartnerId}>
                <SelectTrigger data-testid="migrate-partner-select">
                  <SelectValue placeholder="Escolha o parceiro destino" />
                </SelectTrigger>
                <SelectContent>
                  {availablePartners.length === 0 ? (
                    <SelectItem value="none" disabled>
                      Nenhum parceiro disponível
                    </SelectItem>
                  ) : (
                    availablePartners.map(partner => (
                      <SelectItem key={partner.id} value={partner.id}>
                        {partner.name} {partner.company_name ? `(${partner.company_name})` : ''}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Preview da migração */}
          {targetPartner && (
            <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span className="text-sm">{selectedClients.length} cliente(s)</span>
                </div>
                <ArrowRight className="w-4 h-4 text-green-600" />
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 font-medium">
                  <Users className="w-4 h-4" />
                  <span>{targetPartner.name}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            data-testid="migrate-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleMigrate}
            disabled={!targetPartnerId || loading || selectedClients.length === 0}
            data-testid="migrate-confirm-button"
          >
            {loading ? 'Migrando...' : `Migrar ${selectedClients.length} Cliente(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MigrateClientsModal;
